import EmptyState from '@/components/common/EmptyState';
import SelectInput from '@/components/Forms/SelectInput';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { User, Video } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    COUNTRIES,
    DAYS,
    DEFAULT_COUNTRY_CODE,
    LEVELS,
    LEVEL_ORDER,
    LevelKey,
    TimeField,
} from './liveSessionsConfig';
import LiveSessionCard from './Partials/LiveSessionCard';

interface LiveSession {
    id: number;
    title: string;
    level: LevelKey;
    teacher: string;
    day: string;
    day_order: number;
    time_es: string;
    time_ar: string;
    time_co: string;
    time_mx: string;
    time_ec: string;
    meet_url: string | null;
    active: boolean;
    status: string;
}

interface Props {
    live_sessions: LiveSession[];
}

const ALL_LEVELS = 'all';

// Times arrive raw (e.g. "01:00*"); the trailing "*" means the class falls on the
// next day in that timezone.
const parseTime = (raw: string): { display: string; nextDay: boolean } => {
    const nextDay = raw.endsWith('*');
    return { display: nextDay ? raw.slice(0, -1) : raw, nextDay };
};

const LiveSessionsContent: React.FC<Props> = ({ live_sessions }) => {
    const { t } = useTranslation();
    const [levelFilter, setLevelFilter] = useState<
        LevelKey | typeof ALL_LEVELS
    >(ALL_LEVELS);
    const [countryCode, setCountryCode] = useState<string>(() => {
        const saved = localStorage.getItem('liveSessions.countryCode');
        return COUNTRIES.some((c) => c.code === saved)
            ? saved!
            : DEFAULT_COUNTRY_CODE;
    });

    const country =
        COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];
    const timeField: TimeField = country.timeField;

    const sessionsByDay = useMemo(() => {
        const filtered = live_sessions.filter(
            (session) =>
                levelFilter === ALL_LEVELS || session.level === levelFilter
        );
        return DAYS.map((day) => ({
            day,
            sessions: filtered
                .filter((session) => session.day_order === day.order)
                .sort((a, b) =>
                    (a[timeField] || '').localeCompare(b[timeField] || '')
                ),
        }));
    }, [live_sessions, levelFilter, timeField]);

    const hasSessions = live_sessions.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Live Sessions" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <GlassCard className="p-6 shadow-lg">
                    <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        🎌 {t('liveSessions.title')}
                    </h1>
                    <ul className="space-y-2 text-gray-700 dark:text-neutral-300">
                        <li>{t('liveSessions.intro.line1')}</li>
                        <li>✅ {t('liveSessions.intro.line2')}</li>
                        <li>✅ {t('liveSessions.intro.line3')}</li>
                        <li>❗ {t('liveSessions.intro.line4')}</li>
                    </ul>
                    <div className="border-theme-primary bg-theme-lightest mt-4 rounded-lg border-l-4 p-3 text-sm text-gray-700 dark:text-neutral-300">
                        💡 {t('liveSessions.intro.recommendation')}
                    </div>
                </GlassCard>

                {/* Levels legend */}
                <GlassCard className="p-6 shadow-lg">
                    <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                        🎓 {t('liveSessions.legendTitle')}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {LEVEL_ORDER.map((key) => {
                            const level = LEVELS[key];
                            const cefr = level.cefr.includes('.')
                                ? t(level.cefr)
                                : level.cefr;
                            return (
                                <GlassCard
                                    key={key}
                                    className="flex flex-col overflow-hidden shadow-sm"
                                >
                                    <div
                                        className="h-1 w-full"
                                        style={{ backgroundColor: level.color }}
                                    />
                                    <div className="flex flex-col gap-1.5 p-3">
                                        <span
                                            className="self-start rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                                            style={{
                                                backgroundColor: level.color,
                                            }}
                                        >
                                            {t(level.labelKey)}
                                        </span>
                                        <div className="text-xs font-medium text-gray-500 dark:text-neutral-400">
                                            {cefr}
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-neutral-400">
                                            {t(level.descKey)}
                                        </p>
                                    </div>
                                </GlassCard>
                            );
                        })}
                    </div>
                </GlassCard>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <SelectInput
                        label={`🎓 ${t('liveSessions.filterLevel')}`}
                        name="level_filter"
                        value={levelFilter}
                        onChange={(v) =>
                            setLevelFilter(v as LevelKey | typeof ALL_LEVELS)
                        }
                    >
                        <option value={ALL_LEVELS}>
                            {t('liveSessions.allLevels')}
                        </option>
                        {LEVEL_ORDER.map((key) => (
                            <option key={key} value={key}>
                                {t(LEVELS[key].labelKey)}
                            </option>
                        ))}
                    </SelectInput>
                    <SelectInput
                        label={`📍 ${t('liveSessions.filterCountry')}`}
                        name="country_code"
                        value={countryCode}
                        onChange={(v) => {
                            localStorage.setItem(
                                'liveSessions.countryCode',
                                v
                            );
                            setCountryCode(v);
                        }}
                    >
                        {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                                {c.flag} {c.label}
                            </option>
                        ))}
                    </SelectInput>
                </div>

                {/* Weekly grid */}
                {hasSessions ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
                        {sessionsByDay.map(({ day, sessions }) => (
                            <div
                                key={day.key}
                                className="relative flex flex-col space-y-3 sm:after:absolute sm:after:top-0 sm:after:-right-3 sm:after:h-full sm:after:w-px sm:after:bg-black/10 sm:after:content-[''] sm:dark:after:bg-white/10 sm:[&:nth-child(2n)]:after:hidden lg:[&:nth-child(2n)]:after:block lg:[&:nth-child(5n)]:after:hidden"
                            >
                                <h3 className="text-center text-lg font-bold text-gray-900 dark:text-white">
                                    {t(day.labelKey)}
                                </h3>
                                {sessions.map((session) => {
                                    const { display, nextDay } = parseTime(
                                        session[timeField] || ''
                                    );
                                    return (
                                        <LiveSessionCard
                                            key={session.id}
                                            level={session.level}
                                            title={session.title}
                                            className="h-[200px]"
                                        >
                                            <div className="flex flex-col gap-2">
                                                {session.teacher ? (
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-neutral-300">
                                                        <User className="h-3.5 w-3.5 shrink-0" />
                                                        <span>
                                                            {session.teacher}
                                                        </span>
                                                    </div>
                                                ) : null}
                                                <div className="text-xs font-semibold text-gray-900 dark:text-white">
                                                    {country.flag} {display}
                                                    {nextDay ? (
                                                        <span className="ml-1 font-normal text-gray-500 dark:text-neutral-400">
                                                            (
                                                            {t(
                                                                'liveSessions.nextDayNote'
                                                            )}
                                                            )
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {session.meet_url ? (
                                                    <a
                                                        href={session.meet_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-theme-primary text-theme-on-primary hover:bg-theme-primary-hover mt-auto block w-full rounded-lg px-3 py-2 text-center text-sm font-semibold transition-colors"
                                                    >
                                                        {t(
                                                            'liveSessions.joinButton'
                                                        )}
                                                    </a>
                                                ) : null}
                                            </div>
                                        </LiveSessionCard>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState
                        icon={Video}
                        description={t('liveSessions.empty')}
                    />
                )}
            </div>
        </AppLayout>
    );
};

const LiveSessions: React.FC<Props> = ({ live_sessions }) => (
    <ThemeWrapper>
        <LiveSessionsContent live_sessions={live_sessions} />
    </ThemeWrapper>
);

export default LiveSessions;
